import { Deferred, Head } from '@inertiajs/react'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Button, Card, CardTitle, DescriptionList, DescriptionTerm, DescriptionDetails } from '@/components/ui'
import { TransactionStatusBadge } from '@/components/common/TransactionStatusBadge'
import { TransactionActivity } from '@/components/common/TransactionActivity'
import { GatewayNotice } from '@/components/common/GatewayNotice'
import { CardSkeleton } from '@/components/common/Skeletons'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDateTime } from '@/lib/format'
import type { GatewayState, GatewayError, PayoutRecord, ActivityRow } from '@/types'

interface PayoutShowProps {
  payout?: { record: PayoutRecord | null; error: GatewayError | null }
  activity: ActivityRow[]
  gateway: GatewayState
}

function PayoutShow({ payout, activity, gateway }: PayoutShowProps) {
  const { t, locale } = useTranslations()
  const record = payout?.record ?? null

  return (
    <>
      <Head title={t('payouts.detail_title')} />
      <PageHeading title={t('payouts.detail_title')} actions={<Button plain href="/payouts">{t('common.back')}</Button>} />

      <div className="mt-6 flex flex-col gap-6">
        <GatewayNotice state={gateway} />

        {gateway.configured && (
          <Deferred data="payout" fallback={<CardSkeleton lines={4} />}>
            {payout?.error ? (
              <GatewayNotice state={{ ...gateway, error: payout.error }} />
            ) : record ? (
              <Card>
                <CardTitle>{t('payments.details')}</CardTitle>
                <DescriptionList className="mt-2">
                  {record.status && (
                    <>
                      <DescriptionTerm>{t('payments.columns.status')}</DescriptionTerm>
                      <DescriptionDetails>
                        <TransactionStatusBadge status={record.status} family={record.status_family} />
                      </DescriptionDetails>
                    </>
                  )}
                  <DescriptionTerm>{t('payments.columns.transaction')}</DescriptionTerm>
                  <DescriptionDetails className="font-mono text-sm">{record.transaction_id || '—'}</DescriptionDetails>

                  <DescriptionTerm>{t('payments.columns.amount')}</DescriptionTerm>
                  <DescriptionDetails>{formatMoney(record.amount, record.currency ?? 'EUR', locale)}</DescriptionDetails>

                  <DescriptionTerm>{t('payments.columns.date')}</DescriptionTerm>
                  <DescriptionDetails>{formatDateTime(record.created_at, locale)}</DescriptionDetails>
                </DescriptionList>
              </Card>
            ) : null}
          </Deferred>
        )}

        <TransactionActivity items={activity} />
      </div>
    </>
  )
}

PayoutShow.layout = withAppLayout

export default PayoutShow
